import { isEmptyString } from '@/utils/string';
import { getGoogleOidcClientId } from './google-oidc-client-id';
import { openOAuthPopup, type OAuthOpenResponse } from './oauth-popup';

/** OAuth providers used by the login SSO flows. */
export const LoginOAuthProvider = {
  GoogleOIDC: 'google-oidc',
  OktaOIDC: 'okta-oidc',
  AzureOIDC: 'azure-oidc',
  GenericOIDC: 'generic-oidc',
} as const;

export type LoginOAuthProvider = (typeof LoginOAuthProvider)[keyof typeof LoginOAuthProvider];

const GOOGLE_AUTHORIZATION_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const OIDC_SCOPE = 'email openid profile';

export type GoogleOIDCOptions = {
  /** Restrict to a Google Workspace hosted domain (`hd`). `*` allows any custom domain. */
  hostedDomain?: string;
  /** Pre-select a Google account (`login_hint`). */
  loginHint?: string;
};

function nonEmptyString(value: string | undefined): string | undefined {
  return isEmptyString(value) ? undefined : value;
}

function buildGoogleOIDCExtraParams({
  hostedDomain,
  loginHint,
}: GoogleOIDCOptions): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    access_type: 'offline',
    include_granted_scopes: true,
  };

  const hd = nonEmptyString(hostedDomain);
  const hint = nonEmptyString(loginHint);

  if (hd != null) {
    params.hd = hd;
  }

  // Force the account chooser unless a specific (non-wildcard) hosted domain is requested.
  if (hd == null || hd === '*') {
    params.prompt = 'select_account';
  }

  if (hint != null) {
    params.login_hint = hint;
  }

  return params;
}

export function openGoogleOIDCPopup(options: GoogleOIDCOptions = {}): Promise<OAuthOpenResponse> {
  return openOAuthPopup({
    provider: LoginOAuthProvider.GoogleOIDC,
    baseUrl: GOOGLE_AUTHORIZATION_URL,
    clientId: getGoogleOidcClientId(),
    scope: OIDC_SCOPE,
    extraParams: buildGoogleOIDCExtraParams(options),
  });
}

export type DynamicOIDCOptions = {
  /** Authorization endpoint resolved from the IdP's OpenID configuration. */
  authorizationURL: string;
  clientID: string;
};

function openDynamicOIDCPopup(
  provider: LoginOAuthProvider,
  { authorizationURL, clientID }: DynamicOIDCOptions,
): Promise<OAuthOpenResponse> {
  return openOAuthPopup({
    provider,
    baseUrl: authorizationURL,
    clientId: clientID,
    scope: OIDC_SCOPE,
  });
}

export function openOktaOIDCPopup(options: DynamicOIDCOptions): Promise<OAuthOpenResponse> {
  return openDynamicOIDCPopup(LoginOAuthProvider.OktaOIDC, options);
}

export function openAzureOIDCPopup(options: DynamicOIDCOptions): Promise<OAuthOpenResponse> {
  return openDynamicOIDCPopup(LoginOAuthProvider.AzureOIDC, options);
}

export function openGenericOIDCPopup(options: DynamicOIDCOptions): Promise<OAuthOpenResponse> {
  return openDynamicOIDCPopup(LoginOAuthProvider.GenericOIDC, options);
}

type OpenIDConfiguration = {
  authorization_endpoint: string;
};

/**
 * Resolve the authorization endpoint for an OIDC issuer from its published
 * `.well-known/openid-configuration` document. Okta/Azure/Generic OIDC IdPs
 * advertise their authorization endpoint here rather than at a fixed URL, so
 * it's fetched at login time before opening the popup.
 */
export async function fetchOidcAuthorizationEndpoint(issuerUrl: string): Promise<string> {
  const base = issuerUrl.replace(/\/$/, '');
  const response = await fetch(`${base}/.well-known/openid-configuration`);

  if (!response.ok) {
    throw new Error(`Failed to fetch OpenID configuration from ${base} (${response.status})`);
  }

  const config = (await response.json()) as OpenIDConfiguration;

  if (isEmptyString(config.authorization_endpoint)) {
    throw new Error(`OpenID configuration from ${base} is missing an authorization_endpoint`);
  }

  return config.authorization_endpoint;
}
